import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useResumoVendasDia, useSegmentoDoUsuario } from '@/hooks/useResumoVendasDia';
import { useVendasIaKpi } from '@/hooks/useVendasIaKpi';
import { useQualidadePainelTopo } from '@/hooks/useQualidadePainelTopo';
import { resumirVendasIa } from '@/lib/vendasIa';
import { ModalVendasIa } from '@/components/isabella/ModalVendasIa';
import { useFaturasBeeKpi } from '@/hooks/useFaturasBeeKpi';
import { useDiscadoraKpiTopo } from '@/hooks/useDiscadoraKpiTopo';
import { useNotasBkoEquipe } from '@/hooks/useNotasBkoEquipe';
import { useNotaBkoMes } from '@/hooks/useNotaBkoMes';
import { PainelTarefasTopo } from '@/components/tarefas/PainelTarefasTopo';
import { PainelSmsTopo, usePainelSms } from '@/components/sms/PainelSmsTopo';
import { PainelFaturasTopo } from '@/components/faturas/PainelFaturasTopo';
import { useTarefasPainel } from '@/hooks/useTarefas';
import { NotaBkoDetalheModal } from '@/components/bko/NotaBkoDetalheModal';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { NotaPrimeValor } from '@/components/bko/NotaPrimeValor';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TrendingUp, Wifi, RefreshCw, Smartphone, DollarSign, MessageCircle, FileText, PhoneCall, CalendarCheck, Star, Fingerprint, PackageSearch, Mail, BotMessageSquare, ShieldCheck, MessageSquareText } from 'lucide-react';
import { useBiometriaKpiTopo } from '@/hooks/useBiometriaKpiTopo';
import { TriagemBiometriaModal } from '@/components/kanban/biometria/TriagemBiometriaModal';
import { useAparelhosKpiTopo } from '@/hooks/useAparelhosKpiTopo';
import { ModalAparelhosDisponiveis } from '@/components/aparelhos/ModalAparelhosDisponiveis';

type Tone = 'green' | 'amber' | 'red' | 'blue' | 'violet' | 'orange' | 'gold';

/** (Design v3, Douglas 16/08 — substitui os chips coloridos de 15/07.)
 * Ícone SEMPRE neutro; número neutro por padrão. Cor só quando o chip exige
 * AÇÃO AGORA: vermelho (fila parada) e âmbar (abaixo da meta). Assim o único
 * número colorido da faixa é o que precisa de olho. */
const TONE_TEXT: Record<Tone, string> = {
  green: 'text-primary dark:text-[hsl(var(--brand-header))]',
  amber: 'text-primary dark:text-[hsl(var(--brand-header))]',
  red: 'text-primary dark:text-[hsl(var(--brand-header))]',
  blue: 'text-primary dark:text-[hsl(var(--brand-header))]',
  violet: 'text-primary dark:text-[hsl(var(--brand-header))]',
  orange: 'text-primary dark:text-[hsl(var(--brand-header))]',
  gold: 'text-primary dark:text-[hsl(var(--brand-header))]',
};
const TONE_ICON: Record<Tone, string> = {
  green: 'text-muted-label', amber: 'text-muted-label', red: 'text-muted-label',
  blue: 'text-muted-label', violet: 'text-muted-label', orange: 'text-muted-label', gold: 'text-muted-label',
};

interface ChipProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  total: number | string;
  tone: Tone;
  pulse?: boolean;
  onClick: () => void;
  title?: string;
  /** Quando presente, mostra um tooltip rico (mini-tabela) no hover. */
  tooltipContent?: React.ReactNode;
  /** Segundo número, discreto, depois do rótulo. Ex.: o "24,2%" do chip Faturas. */
  suffix?: string;
}

const Chip: React.FC<ChipProps> = ({ icon: Icon, label, total, tone, pulse, onClick, title, tooltipContent, suffix }) => {
  const btn = (
    <button
      type="button"
      onClick={onClick}
      title={tooltipContent ? undefined : (title || `${label}: ${total}${suffix ? ` · ${suffix}` : ''}`)}
      className={cn(
        'inline-flex items-center gap-[5px] h-full px-[7px] text-[11px] font-medium whitespace-nowrap',
        'relative before:absolute before:left-0 before:top-1/2 before:h-[15px] before:w-px before:-translate-y-1/2 before:bg-border first:before:hidden',
        'text-foreground/75 hover:text-foreground',
        'hover:bg-accent/60 transition-colors',
      )}
    >
      <Icon className={cn('h-[10px] w-[10px]', TONE_ICON[tone], pulse && 'animate-pulse')} />
      <span className={cn('font-display text-[12.5px] font-bold tabular-nums tracking-tight', TONE_TEXT[tone])}>{total}</span>
      <span className="hidden xl:inline text-muted-foreground">{label}</span>
      {suffix && (
        // O sufixo só em tela larga (Douglas 02/10/2026: "não está cabendo tudo
        // no topo"): no resto, ele vai no title/tooltip e o chip fica curto.
        <span className="hidden min-[1800px]:inline text-muted-foreground/70 tabular-nums">· {suffix}</span>
      )}
    </button>
  );
  if (!tooltipContent) return btn;
  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>{btn}</TooltipTrigger>
        {/* O CONTEUDO DO TOOLTIP TAMBEM CLICA (Douglas 21/08/2026: "a nota eu clico
            e nao vai, nao abre o modal"). O tooltip abre logo ABAIXO do chip e
            cobre a area: quem le a lista ja esta com o mouse em cima dele, e o
            clique morria ali — o botao ficava atras. Varios desses tooltips
            dizem "clique p/ abrir", entao clicar neles tem que funcionar. */}
        <TooltipContent
          side="bottom"
          align="end"
          className="p-0 border-0 bg-transparent shadow-none cursor-pointer"
          onClick={onClick}
        >
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const NotaPrimeHeaderChip: React.FC<{
  valor: number | string;
  label: string;
  onClick: () => void;
  title: string;
}> = ({ valor, label, onClick, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className="relative inline-flex h-full items-center gap-1.5 whitespace-nowrap px-2.5 text-foreground/75 transition-colors before:absolute before:left-0 before:top-1/2 before:h-[15px] before:w-px before:-translate-y-1/2 before:bg-border first:before:hidden hover:bg-accent/60 hover:text-foreground"
  >
    <NotaPrimeValor valor={valor} size="sm" />
    <span className="hidden text-[11px] text-muted-foreground xl:inline">{label}</span>
  </button>
);

/** Faturamento abreviado: 8500 → "R$ 8.5k", 1250000 → "R$ 1.25M" */
function abreviarFaturamento(valor: number): string {
  if (!valor) return 'R$ 0';
  if (valor >= 1_000_000) {
    return `R$ ${(valor / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
  }
  if (valor >= 1_000) {
    return `R$ ${(valor / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  }
  return `R$ ${valor.toFixed(0)}`;
}

/**
 * Chips compactos exibidos no header global.
 *
 * KPIs de vendas do dia: Altas, Fibra, REN, TT, FAT.
 *   - Dados via RPC gerar_resumo_dia_venda (mesma fonte da msg automática
 *     de venda fechada — garante consistência).
 *   - Click leva pra dashboard de Venda Diária.
 *
 * Os chips "Novas"/"Minhas" do WhatsApp Inbox foram removidos: viviam em
 * 99+ e não agregavam informação útil no header.
 */
export function WhatsAppInboxChips() {
  /* ⛔ A BARRA DO TOPO E' PADRAO DO CRM — igual em TODA pagina.
     (Douglas 21/08/2026: "isso e padrao do crm, nao pode ter trava ou algo
     atrapalhando".)

     Aqui existia uma variavel que olhava a rota (`pathname.startsWith
     ('/dashboards')`) e escondia SEIS chips: Finalizados, Altas, Fibra, REN, TT
     e FAT. Nasceu de um ajuste meu de layout na Visao Prime — la os tiles
     repetem os mesmos numeros a 40px de distancia e a barra apertava a busca.

     Dois problemas. Barra que muda de pagina pra pagina nao e' padrao: quem olha
     nao sabe se o numero sumiu ou zerou. E o teste era pela ROTA, entao pegava
     TODAS as abas de /dashboards — inclusive o Backoffice Prime, que nao tem
     tile nenhum desses numeros e perdia os seis a toa.

     Se um dia a barra apertar a busca, o conserto e' no espaco (largura,
     responsividade do label) — NUNCA escondendo numero em algumas telas. */
  const { profile } = useAuth();
  const navigate = useNavigate();
  const bio = useBiometriaKpiTopo();
  const [triagemBioAberta, setTriagemBioAberta] = React.useState(false);
  const aparelhos = useAparelhosKpiTopo();
  const [aparelhosAberto, setAparelhosAberto] = React.useState(false);
  // ⛔ CPF e CNPJ nunca somam: cada um vê o SEU (Douglas 21/09/2026). O chip
  // segue a equipe de quem está logado — Conecta vê CPF, o resto vê CNPJ — e o
  // title diz qual é, para ninguém ler o número achando que é a casa inteira.
  const segmentoVendas = useSegmentoDoUsuario();
  const { data: vendas } = useResumoVendasDia(segmentoVendas);
  const marcaVendas = segmentoVendas === 'PF' ? 'Conecta · CPF' : 'Prime · CNPJ';
  const { data: faturasBee } = useFaturasBeeKpi();
  // Placar do dia da DISCADORA PRIME (motor) — chip laranja (cor do Asterisk).
  const { data: disc } = useDiscadoraKpiTopo();

  // NOTA DE CADA UM no topo (Douglas 21/08/2026): "coloque a nota deles
  // mesmo. e para o master a nota de todos em lista de nomes." Quem é do Backoffice vê a sua; gestão vê a do time.
  // O cabeçalho observa apenas o último resultado desta sessão. Calcular a
  // equipe inteira aqui bloqueava o carregamento das telas mesmo sem abrir notas.
  const { data: notasBko } = useNotasBkoEquipe(undefined, false);
  const { data: tarefasPainel } = useTarefasPainel(30, null);
  const [painelTarefasAberto, setPainelTarefasAberto] = React.useState(false);
  const { data: smsPainel } = usePainelSms(30, null);
  const [painelSmsAberto, setPainelSmsAberto] = React.useState(false);
  const [painelFaturasAberto, setPainelFaturasAberto] = React.useState(false);
  const podeFiltrarEquipeTarefas = ['master', 'diretor', 'gerente'].includes(String(profile?.role || '').toLowerCase())
    || /GERENTE|DIRETOR/.test(String(profile?.funcao || '').toUpperCase());
  const [minhaNotaAberta, setMinhaNotaAberta] = React.useState(false);
  const [competenciaMinhaNota, setCompetenciaMinhaNota] = React.useState(() => new Date());
  const minhaLinha = (notasBko || []).find((l) => l.usuario_id === profile?.id) || null;
  const { data: minhaNota, isError: erroMinhaNota, refetch: recarregarMinhaNota } = useNotaBkoMes(
    minhaNotaAberta ? minhaLinha?.usuario_id ?? null : null,
    minhaLinha?.tipo ?? null,
    competenciaMinhaNota,
  );
  const podeVerKpiGestao = ['master', 'diretor', 'gerente', 'qualidade'].includes(
    String(profile?.role || '').toLowerCase(),
  );
  // VENDAS DA ISABELLA (Douglas 26/09/2026): "não usamos mais este controle
  // (Transf. IA), troque isso pelas vendas IA". O chip de transferências e o
  // par dele (Finalizados) saíram; este lê o mapa v_isabella_vendas_linhas.
  const { data: vendasIa } = useVendasIaKpi(podeVerKpiGestao);
  const [vendasIaAberto, setVendasIaAberto] = React.useState(false);
  // QUALIDADE (Douglas 28/09/2026): do contrato à fatura, uma linha por pessoa.
  const podeVerQualidade = podeVerKpiGestao || String(profile?.funcao || '').toUpperCase() === 'SUPERVISORA_BACKOFFICE';
  const { data: qualidadeTopo } = useQualidadePainelTopo(podeVerQualidade);
  const qualidadeNoPiso = (qualidadeTopo?.linhas ?? []).filter((l) => l.bateu).length;
  const resumoIa = React.useMemo(
    () => (vendasIa ? resumirVendasIa(vendasIa.vendas, vendasIa.hoje) : null),
    [vendasIa],
  );
  const faturasBeeHoje = faturasBee?.hoje ?? 0;
  if (!profile?.id) return null;
  // Esconde para BKO_PORTABILIDADE — eles usam BkoPortabilidadeChipsInline.
  if (profile?.funcao === 'BKO_PORTABILIDADE') return null;

  const goVendaDiaria = () => navigate(segmentoVendas === 'PF' ? '/dashboards?tab=venda-diaria&segmento=PF' : '/dashboards?tab=venda-diaria');

  const discAtivo = !!(disc && disc.discadas > 0);

  return (
    <div className="flex items-center h-full overflow-x-auto scrollbar-hide divide-x divide-border">
      {/* DISCADORA PRIME (motor) — % de contato do dia; laranja = cor do Asterisk */}
      {discAtivo && (
        <Chip
          icon={PhoneCall}
          label="Discadora"
          total={`${disc!.pct}%`}
          tone="orange"
          onClick={() => navigate('/discadora')}
          tooltipContent={
            <div className="rounded-lg border bg-popover text-popover-foreground shadow-md p-2.5 min-w-[200px] text-[11px]">
              <div className="flex items-center justify-between font-semibold mb-1.5">
                <span className="flex items-center gap-1"><PhoneCall className="h-3 w-3 text-warning" /> Discadora Prime · hoje</span>
                <span className="text-[9px] font-normal text-muted-foreground">clique p/ abrir</span>
              </div>
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Discadas</span><span className="font-semibold tabular-nums">{disc!.discadas}</span></div>
              <div className="flex justify-between py-0.5 text-primary dark:text-primary"><span>Falou com gente</span><span className="font-semibold tabular-nums">{disc!.humanos}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Caixas barradas</span><span className="font-semibold tabular-nums">{disc!.caixas}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Não atenderam</span><span className="font-semibold tabular-nums">{disc!.nao_atendeu}</span></div>
              {disc!.abandonos > 0 && (
                <div className="flex justify-between py-0.5 text-warning dark:text-warning"><span>Abandonos</span><span className="font-semibold tabular-nums">{disc!.abandonos}</span></div>
              )}
              {disc!.porConsultor.length > 0 && (
                <>
                  <div className="border-t mt-1.5 pt-1 mb-0.5 text-[9px] uppercase tracking-wide text-muted-foreground">Falou com gente · por consultor</div>
                  {disc!.porConsultor.slice(0, 8).map((c) => (
                    <div key={c.nome} className="flex justify-between py-0.5 gap-3"><span className="truncate max-w-[135px]">{c.nome}</span><span className="tabular-nums">{c.qtd}</span></div>
                  ))}
                </>
              )}
            </div>
          }
        />
      )}
      {/* Bio: os pedidos parados na BIOMETRIA de quem está olhando. Abre a
          própria Triagem Biometria aqui, sem sair da tela — é a fila que o
          comercial mais precisa trabalhar no telefone (Douglas 06/08). */}
      {bio.pedidos > 0 && (
        <>
          <Chip
            icon={Fingerprint}
            label="Bio"
            total={bio.pedidos}
            tone="red"
            pulse={bio.horasMaisAntigo >= 24}
            onClick={() => setTriagemBioAberta(true)}
            tooltipContent={
              <div className="rounded-lg border bg-popover text-popover-foreground shadow-md p-2.5 min-w-[195px] text-[11px]">
                <div className="flex items-center gap-1 font-semibold mb-1.5">
                  <Fingerprint className="h-3 w-3 text-danger" /> Triagem Biometria
                </div>
                <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Pedidos parados</span><span className="font-semibold tabular-nums text-danger">{bio.pedidos}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Itens</span><span className="font-semibold tabular-nums">{bio.itens}</span></div>
                <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Mais antigo</span><span className="font-semibold tabular-nums">{bio.horasMaisAntigo}h parado</span></div>
                {/* Acompanhamento: quem JA' foi trabalhado depois de cair na coluna.
                    Conta ligação gravada e mensagem enviada de verdade — não clique. */}
                <div className="border-t border-border mt-1.5 pt-1.5">
                  <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Já ligaram</span><span className="font-semibold tabular-nums text-primary">{bio.comLigacao}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Já mandaram WhatsApp</span><span className="font-semibold tabular-nums text-primary">{bio.comWhatsapp}</span></div>
                  <div className="flex justify-between py-0.5"><span className="text-muted-foreground font-semibold">Sem nenhum contato</span><span className="font-bold tabular-nums text-danger">{bio.semContato}</span></div>
                </div>
                <div className="text-[9px] text-muted-foreground pt-1">Conta ligação gravada e mensagem enviada depois de entrar na fila · clique abre a triagem</div>
              </div>
            }
          />
          <TriagemBiometriaModal isOpen={triagemBioAberta} onClose={() => setTriagemBioAberta(false)} />
        </>
      )}

      {/* Aparelhos: o que a Prime consegue entregar HOJE. O consultor tinha que
          ir em Configuracoes ▸ Produtos — tela de gestor — pra saber se tinha
          aparelho; o chip traz isso pra barra de sempre e o clique abre a lista
          por cima da tela em que ele esta'. (Douglas 09/08)
          O numero e' MODELO COM ESTOQUE, nao o catalogo: sao 134 modelos na
          tabela do mes e ~39 com aparelho no deposito. */}
      {aparelhos.modelosComEstoque > 0 && (
        <>
          <Chip
            icon={PackageSearch}
            label="Aparelhos"
            total={aparelhos.modelosComEstoque}
            tone="violet"
            onClick={() => setAparelhosAberto(true)}
            tooltipContent={
              <div className="rounded-lg border bg-popover text-popover-foreground shadow-md p-2.5 min-w-[210px] text-[11px]">
                <div className="flex items-center gap-1 font-semibold mb-1.5">
                  <PackageSearch className="h-3 w-3 text-primary" /> Aparelhos disponíveis
                </div>
                {aparelhos.topModelos.map((m) => (
                  <div key={m.nome} className="flex justify-between gap-3 py-0.5">
                    <span className="text-muted-foreground truncate">{m.nome}</span>
                    <span className="font-semibold tabular-nums text-primary shrink-0">{m.quantidade.toLocaleString('pt-BR')}</span>
                  </div>
                ))}
                <div className="border-t border-border mt-1.5 pt-1.5 flex justify-between">
                  <span className="text-muted-foreground font-semibold">Total em estoque</span>
                  <span className="font-bold tabular-nums text-primary">{aparelhos.unidades.toLocaleString('pt-BR')} un</span>
                </div>
                <div className="text-[9px] text-muted-foreground pt-1">
                  {aparelhos.dataFoto
                    ? `Foto de estoque de ${aparelhos.dataFoto.split('-').reverse().join('/')}`
                    : 'Sem foto de estoque'}
                  {aparelhos.diasDaFoto != null && aparelhos.diasDaFoto > 1 && ` — ${aparelhos.diasDaFoto} dias atrás`}
                  {' '}· clique abre a lista
                </div>
              </div>
            }
          />
          <ModalAparelhosDisponiveis aberto={aparelhosAberto} onFechar={setAparelhosAberto} />
        </>
      )}

      {/* VENDAS DA ISABELLA — no lugar das transferências da IA (26/09/2026).
          Número = vendas registradas por ela no mês, sem percentual (Douglas 26/09).
          Clique abre o controle (ModalVendasIa): o que espera alguém lançar. */}
      {podeVerKpiGestao && resumoIa && vendasIa && (resumoIa.mes.vendas > 0 || resumoIa.mes.naoEraDela > 0) && (
        <>
          <Chip
            icon={BotMessageSquare}
            label="Vendas IA"
            total={resumoIa.mes.vendas}
            tone="red"
            onClick={() => setVendasIaAberto(true)}
            tooltipContent={
              <div className="rounded-lg border bg-popover text-popover-foreground shadow-md p-2.5 min-w-[230px] text-[11px]">
                <div className="flex items-center justify-between font-semibold mb-1.5">
                  <span className="flex items-center gap-1"><BotMessageSquare className="h-3 w-3 text-primary" /> Vendas da IsabellA</span>
                  <span className="text-[9px] font-normal text-muted-foreground">clique p/ abrir</span>
                </div>
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 text-[9px] uppercase tracking-wider text-muted-foreground pb-0.5">
                  <span /><span className="text-right">vendas</span><span className="text-right">pedido</span>
                </div>
                {([['Hoje', resumoIa.hoje], ['Ontem', resumoIa.ontem], ['Últimos 7 dias', resumoIa.semana], ['No mês', resumoIa.mes]] as const).map(([rotulo, p]) => (
                  <div key={rotulo} className="grid grid-cols-[1fr_auto_auto] gap-x-3 py-0.5 tabular-nums">
                    <span className="text-muted-foreground">{rotulo}</span>
                    <span className="text-right font-semibold">{p.vendas}</span>
                    <span className={cn('text-right font-semibold', p.pedido > 0 && 'text-[#075E54] dark:text-[#25D366]')}>{p.pedido}</span>
                  </div>
                ))}
                <div className="border-t mt-1.5 pt-1 flex justify-between py-0.5 text-[#7A2235] dark:text-[#C87484]">
                  <span>Esperando pedido</span><span className="font-semibold tabular-nums">{resumoIa.mes.esperando}</span>
                </div>
                <div className="flex justify-between py-0.5 text-muted-foreground">
                  <span>Não eram dela (anuladas)</span><span className="tabular-nums">{resumoIa.mes.naoEraDela}</span>
                </div>
                <div className="border-t mt-1.5 pt-1 mb-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">No mês · linhas · em pedido</div>
                {resumoIa.categoriasMes.map((c) => (
                  <div key={c.chave} className="flex justify-between py-0.5 gap-3 tabular-nums">
                    <span>{c.rotulo}</span><span>{c.linhas} · {c.pedido}</span>
                  </div>
                ))}
              </div>
            }
          />
          <ModalVendasIa aberto={vendasIaAberto} onFechar={setVendasIaAberto} hoje={vendasIa.hoje} vendas={vendasIa.vendas} />
        </>
      )}
      {podeVerQualidade && qualidadeTopo && qualidadeTopo.linhas.length > 0 && (
        <Chip
          icon={ShieldCheck}
          label="Qualidade"
          total={`${qualidadeNoPiso}/${qualidadeTopo.linhas.length}`}
          tone="red"
          onClick={() => navigate('/visao-comissoes')}
          tooltipContent={
            <div className="rounded-lg border bg-popover text-popover-foreground shadow-md p-2.5 w-[min(460px,calc(100vw-24px))] text-[11px]">
              <div className="flex items-center justify-between font-semibold mb-1.5">
                <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-primary" /> Qualidade · do contrato à fatura</span>
                <span className="text-[9px] font-normal text-muted-foreground">clique p/ abrir</span>
              </div>
              <div className="grid grid-cols-[1fr_repeat(5,auto)] gap-x-3 text-[9px] uppercase tracking-wider text-muted-foreground pb-0.5">
                <span /><span className="text-right">hoje</span><span className="text-right">no prazo</span><span className="text-right">fora</span><span className="text-right">% · piso</span><span className="text-right">R$</span>
              </div>
              {qualidadeTopo.linhas.map((l) => {
                const travado = !!qualidadeTopo.trava?.ativo && !qualidadeTopo.trava?.ok;
                const recebe = l.bateu && !travado;
                return (
                  <div key={l.usuario_id} className="grid grid-cols-[1fr_repeat(5,auto)] gap-x-3 py-0.5 tabular-nums">
                    <span className="truncate">{l.nome.split(' ')[0].charAt(0) + l.nome.split(' ')[0].slice(1).toLowerCase()} <span className="text-muted-foreground">{l.fila}</span></span>
                    <span className="text-right font-semibold">{l.hoje}</span>
                    <span className="text-right">{l.no_prazo ?? '·'}</span>
                    <span className="text-right">{l.fora ?? '·'}</span>
                    <span className={cn('text-right', l.bateu ? 'text-success' : 'text-primary')}>
                      {l.pct == null ? '·' : `${Number(l.pct).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}`}{l.piso != null ? ` · ${Number(l.piso)}` : ''}
                    </span>
                    <span className={cn('text-right', recebe ? 'font-semibold text-foreground' : 'text-muted-foreground/60 line-through')}>
                      {Math.round(l.valor).toLocaleString('pt-BR')}
                    </span>
                  </div>
                );
              })}
              {qualidadeTopo.trava?.ativo && (
                <div className={cn('border-t mt-1.5 pt-1', qualidadeTopo.trava.ok ? 'text-muted-foreground' : 'text-primary')}>
                  Retenção {qualidadeTopo.trava.realizado ?? 0} de {qualidadeTopo.trava.minimo ?? 0}{qualidadeTopo.trava.ok ? ' · liberada' : ' · travado'}
                </div>
              )}
            </div>
          }
        />
      )}
      {minhaLinha && (
        <NotaPrimeHeaderChip
          valor={minhaLinha.nota}
          label="Minha Prime"
          onClick={() => setMinhaNotaAberta(true)}
          title={`Última Nota Prime consultada: ${minhaLinha.nota}. Clique para atualizar e acompanhar os pilares.`}
        />
      )}
      {/* TAREFAS NO LUGAR DA NOTA PRIME (Douglas 30/09/2026): "no lugar da nota
          quero ver quem está abrindo as tarefas, e pra quem, e quem não tem
          finalizada". O número é o que venceu sem finalizar nos últimos 30 dias. */}
      <Chip
        icon={CalendarCheck}
        label="tarefas vencidas"
        total={tarefasPainel ? tarefasPainel.vencidas : '—'}
        suffix={tarefasPainel && tarefasPainel.abertas > 0 ? `${Math.round((tarefasPainel.feitas / tarefasPainel.abertas) * 100)}% feitas` : undefined}
        tone="red"
        onClick={() => setPainelTarefasAberto(true)}
        title="Tarefas: quem abre, quem não finaliza e o que venceu nos últimos 30 dias"
      />
      {/* SMS (Douglas 02/10/2026): o mesmo desenho do painel de Tarefas. O número
          é quantas empresas chamaram no WhatsApp pelo link do SMS; ao lado, o
          aproveitamento sobre as empresas com SMS entregue. */}
      <Chip
        icon={MessageSquareText}
        label="SMS"
        total={smsPainel ? smsPainel.chamaram : '—'}
        suffix={smsPainel && smsPainel.empresas_entregues > 0 ? `${Math.round((smsPainel.chamaram / smsPainel.empresas_entregues) * 100)}% aproveit.` : undefined}
        tone="green"
        onClick={() => setPainelSmsAberto(true)}
        title={`SMS: ${smsPainel ? smsPainel.chamaram : 0} empresas chamaram no WhatsApp pelo link${smsPainel && smsPainel.empresas_entregues > 0 ? ` · ${Math.round((smsPainel.chamaram / smsPainel.empresas_entregues) * 100)}% de aproveitamento` : ''} (30 dias). Clique para ver quem envia e quem chamou.`}
      />
      {/* Faturas: o número grande é o que o Bee DEVOLVEU hoje; o "· 24,2%" ao
          lado é o que de fato CHEGOU no cliente no mês.
          Douglas 17/08: "não adianta saber quantos estamos baixando, mas quantas
          estamos enviando para o e-mail do cliente mesmo. Isso é o que importa."
          Clicar abre o painel das faturas (PainelFaturasTopo), com o link para o Ciclo. */}
      {podeVerKpiGestao && (
        <Chip
          icon={FileText}
          label="Faturas"
          total={faturasBeeHoje}
          suffix={faturasBee && faturasBee.carteira > 0 ? `${faturasBee.pctMes.toFixed(1)}%` : undefined}
          tone="violet"
          onClick={() => setPainelFaturasAberto(true)}
          tooltipContent={
            <div className="rounded-lg border bg-popover text-popover-foreground shadow-md p-2.5 min-w-[210px] text-[11px]">
              <div className="flex items-center gap-1 font-semibold mb-1.5">
                <FileText className="h-3 w-3 text-primary" /> Faturas recebidas pelo Bee
              </div>
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Hoje</span><span className="font-semibold tabular-nums">{faturasBeeHoje}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Ontem</span><span className="font-semibold tabular-nums">{faturasBee?.ontem ?? 0}</span></div>
              <div className="flex justify-between py-0.5"><span className="text-muted-foreground">Últimos 7 dias</span><span className="font-semibold tabular-nums">{faturasBee?.ultimos7 ?? 0}</span></div>
              {faturasBee && faturasBee.carteira > 0 && (
                <>
                  <div className="border-t mt-1.5 pt-1.5">
                    <div className="flex items-center gap-1 font-semibold mb-1">
                      <Mail className="h-3 w-3 text-primary" /> Enviado ao cliente — competência do ciclo
                    </div>
                    <div className="flex justify-between py-0.5">
                      <span className="text-muted-foreground">E-mail enviado</span>
                      <span className="font-semibold tabular-nums">
                        {faturasBee.entreguesMes.toLocaleString('pt-BR')} de {faturasBee.carteira.toLocaleString('pt-BR')}
                      </span>
                    </div>
                    <div className="flex justify-between py-0.5">
                      <span className="text-muted-foreground">da carteira</span>
                      <span className="font-semibold tabular-nums text-primary">{faturasBee.pctMes.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div className="border-t mt-1.5 pt-1 text-[9px] text-muted-foreground">
                    baixar do Bee não é entregar — clique para abrir o painel das faturas
                  </div>
                </>
              )}
            </div>
          }
        />
      )}
      {/* KPIs de vendas do dia (mesma fonte da msg automática) */}
      {vendas && vendas.altas > 0 && (
        <Chip
          icon={TrendingUp}
          label="Altas"
          total={vendas.altas}
          tone="green"
          onClick={goVendaDiaria}
          title={`Altas do dia · ${marcaVendas}: ${vendas.altas}`}
        />
      )}
      {vendas && vendas.fibra > 0 && (
        <Chip
          icon={Wifi}
          label="Fibra"
          total={vendas.fibra}
          tone="blue"
          onClick={goVendaDiaria}
          title={`Fibra do dia · ${marcaVendas}: ${vendas.fibra}`}
        />
      )}
      {vendas && vendas.renovacao > 0 && (
        <Chip
          icon={RefreshCw}
          label="REN"
          total={vendas.renovacao}
          tone="violet"
          onClick={goVendaDiaria}
          title={`Renovações do dia · ${marcaVendas}: ${vendas.renovacao}`}
        />
      )}
      {vendas && vendas.tt > 0 && (
        <Chip
          icon={Smartphone}
          label="TT"
          total={vendas.tt}
          tone="orange"
          onClick={goVendaDiaria}
          title={`TT Móvel do dia · ${marcaVendas}: ${vendas.tt}`}
        />
      )}
      {vendas && vendas.faturamento > 0 && (
        <Chip
          icon={DollarSign}
          label="FAT"
          total={abreviarFaturamento(vendas.faturamento)}
          tone="green"
          onClick={goVendaDiaria}
          title={`Faturamento do dia · ${marcaVendas}: R$ ${vendas.faturamento.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
        />
      )}

      {painelTarefasAberto && <PainelTarefasTopo aberto onFechar={() => setPainelTarefasAberto(false)} podeFiltrarEquipe={podeFiltrarEquipeTarefas} />}
      {painelFaturasAberto && <PainelFaturasTopo aberto onFechar={() => setPainelFaturasAberto(false)} />}
      {painelSmsAberto && <PainelSmsTopo aberto onFechar={() => setPainelSmsAberto(false)} podeFiltrarEquipe={podeFiltrarEquipeTarefas} />}
      {minhaNotaAberta && !minhaNota && (
        <Dialog open onOpenChange={setMinhaNotaAberta}>
          <DialogContent>
            <DialogTitle>Minha Nota Prime</DialogTitle>
            <p className="text-sm text-muted-foreground" role="status">
              {erroMinhaNota ? 'Não foi possível carregar sua nota.' : 'Calculando sua nota…'}
            </p>
            {erroMinhaNota && <button type="button" onClick={() => void recarregarMinhaNota()}>Tentar novamente</button>}
          </DialogContent>
        </Dialog>
      )}
      {minhaNotaAberta && minhaNota && (
        <NotaBkoDetalheModal
          open={minhaNotaAberta}
          onOpenChange={setMinhaNotaAberta}
          nota={minhaNota}
          mostrarSugestoes
          competencia={competenciaMinhaNota}
          onCompetenciaChange={setCompetenciaMinhaNota}
        />
      )}
    </div>
  );
}

export default WhatsAppInboxChips;
